import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { message } from "antd";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { SESSION_CONSTANT } from "../../shared/utils/SessionConstants";
import { AUTHENTICATE_VIEW_CONSTANT, GLOBAL_CONSTANT } from "../../shared/utils/CommonConstants";
import { ENV } from "../../config";
import { getAuthToken } from "./SessionStorageService";
import { msalInstance, API_SCOPE } from "./msalInstance";
import { emitSessionExpired } from "../../utils/sessionEvents";
import { TAX_YEAR_HEADER, getCurrentTaxYear } from "./TaxYearHeader";

const BASE_URL = ENV.API_URL;
const MSAL_TOKEN_FAILED_TOAST_KEY = "msal-token-failed";

let legacyRefreshPromise: Promise<string | undefined> | null = null;

const getSharedLegacyRefresh = (requestHeaders: any): Promise<string | undefined> => {
  if (!legacyRefreshPromise) {
    legacyRefreshPromise = Promise.resolve()
      .then(() => require("../../features/AuthenticateView/services/LoginService"))
      .then(({ refreshToken }) => refreshToken(requestHeaders))
      .finally(() => {
        legacyRefreshPromise = null;
      });
  }
  return legacyRefreshPromise;
};

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  extractData?: boolean;
  skipAuthHeaders?: boolean;
  _retry?: boolean;
}

class ApiService {
  private readonly api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
    });

    this.api.interceptors.request.use(
      async (config) => {
        const skipAuth = (config as ExtendedAxiosRequestConfig).skipAuthHeaders;

        if (!skipAuth) {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            try {
              const response = await msalInstance.acquireTokenSilent({
                scopes: [API_SCOPE],
                account: accounts[0],
              });
              config.headers.Authorization = `Bearer ${response.accessToken}`;
            } catch (error) {
              if (error instanceof InteractionRequiredAuthError) {
                try {
                  const response = await msalInstance.acquireTokenPopup({
                    scopes: [API_SCOPE],
                  });
                  config.headers.Authorization = `Bearer ${response.accessToken}`;
                } catch {
                  emitSessionExpired();
                }
              } else {
                message.error({
                  content: AUTHENTICATE_VIEW_CONSTANT.SSO_TOKEN_REFRESH_FAILED_MESSAGE,
                  key: MSAL_TOKEN_FAILED_TOAST_KEY,
                });
                const AUTH_TOKEN = getAuthToken();
                if (AUTH_TOKEN) config.headers.Authorization = `Bearer ${AUTH_TOKEN}`;
              }
            }
          } else {
            const AUTH_TOKEN = getAuthToken();
            config.headers.Authorization = `Bearer ${AUTH_TOKEN}`;
          }
        }

        const taxYear = getCurrentTaxYear();
        if (taxYear) {
          config.headers[TAX_YEAR_HEADER] = taxYear;
        }

        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        } else if (config.data !== undefined) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config ?? error?.response?.config;
        const message =
          error?.response?.data || error?.message || GLOBAL_CONSTANT.GENERIC_ERROR_MESSAGE;

        if (status === 401 && originalRequest && !originalRequest._retry) {
          if (originalRequest.url?.includes("auth/login")) {
            return Promise.reject(error);
          }

          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            originalRequest._retry = true;
            try {
              const tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: [API_SCOPE],
                account: accounts[0],
              });
              originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
              return this.api.request(originalRequest);
            } catch {
              emitSessionExpired();
              return Promise.reject(error);
            }
          }

          if (!ENV.API_URL || !ENV.APPLICATION_AUTHENTICATION_REFRESH_URL) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const refreshedAccessToken = await getSharedLegacyRefresh(
              originalRequest.headers,
            );
            const accessToken =
              refreshedAccessToken ?? localStorage.getItem(SESSION_CONSTANT.TOKEN_KEY);

            if (accessToken) {
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }

            return this.api.request(originalRequest);
          } catch (refreshError: any) {
            if (refreshError?.response?.status === 401) {
              emitSessionExpired();
            }
            return Promise.reject(refreshError);
          }
        }

        if (
          (status === 401 && !originalRequest) ||
          String(message).includes("UnAuthorizedCustomException")
        ) {
          localStorage.removeItem(SESSION_CONSTANT.TOKEN_KEY);
          window.location.replace("/login");
        }

        if (status === 401) {
          emitSessionExpired();
        }

        return Promise.reject(message);
      },
    );
  }

  protected async makeRequest<T>(
    endpoint: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;

  protected async makeRequest<T>(
    endpoint: string,
    options: ExtendedAxiosRequestConfig & { extractData: true },
  ): Promise<T>;

  protected async makeRequest<T>(
    endpoint: string,
    options?: ExtendedAxiosRequestConfig,
  ): Promise<AxiosResponse<T> | T> {
    try {
      const response = await this.api.request<T>({
        url: endpoint,
        method: options?.method ?? "GET",
        data: options?.data,
        headers: options?.headers,
        params: options?.params,
        responseType: options?.responseType ?? "json",
        skipAuthHeaders: options?.skipAuthHeaders,
      } as ExtendedAxiosRequestConfig);

      return options?.extractData ? response.data : response;
    } catch (e: any) {
      return Promise.reject(e ?? GLOBAL_CONSTANT.GENERIC_ERROR_MESSAGE);
    }
  }
}

export default ApiService;
